import { IProduct } from '@core/api/Products/types'
import { RecommendationTypes } from '@core/api/Recommendations/types'
import SmallBenefits from '@core/components/Benefits/SmallBenefits'
import { LYSCol, LYSContainer, LYSRow } from '@core/components/Primitives'
import ProductDetail from '@core/components/ProductDetail/ProductDetail'
import config from '@core/config/config'
import { usePageContext } from '@core/utils/pages/PageContext'
import { RenderProp, template, Slot } from '@core/utils/templates'
import RecommendationSlider from '@core/components/shared/RecommendationSlider'
import style from './index.module.less'

export interface ProductDetailProps {
  product: IProduct
  variantId: string
}

interface ProductDetailSlots {
  productDetailCategoryBreadcrumbs?: RenderProp<void>
  productInformation?: RenderProp<void>
  productImage?: RenderProp<void>
  productVariationSwitch?: RenderProp<void>
  discontinuedHint?: RenderProp<void>
  price?: RenderProp<void>
  packagingUnitSwitch?: RenderProp<void>
  brandInfo?: RenderProp<void>
  productDescription?: RenderProp<void>
  attachments?: RenderProp<void>
  simpleAttributes?: RenderProp<void>
  smallBenefits?: RenderProp<void>
  awards?: RenderProp<void>
  hazardWarnings?: RenderProp<void>
  frequentlyBoughtTogetherRecommendations?: RenderProp<void>
  followUpProductRelations?: RenderProp<void>
  crossSellProductRelations?: RenderProp<void>
  productBundle?: RenderProp<void>
  lastVisitedPDPsRecommendations?: RenderProp<void>
  sparePartRelation?: RenderProp<void>
}

const ProductDetailLayout = template<ProductDetailProps, ProductDetailSlots>(
  ({ product, slots, ...props }) => {
    const {
      props: { isMobile, isTablet },
    } = usePageContext()
    return (
      <ProductDetail {...props} product={product}>
        <Slot render={slots?.productDetailCategoryBreadcrumbs}>
          <ProductDetail.ProductDetailCategoryBreadcrumbs />
        </Slot>
        <LYSContainer padded={true}>
          {isMobile && (
            <LYSRow>
              <LYSCol>
                <Slot render={slots?.productInformation}>
                  <ProductDetail.ProductInformation />
                </Slot>

                {product.isNew && config.features.newProductsFeature && (
                  <ProductDetail.NewBadge />
                )}
                <Slot render={slots?.productImage}>
                  <ProductDetail.ProductImage className={style.productImage} />
                </Slot>

                <Slot render={slots?.productVariationSwitch}>
                  <ProductDetail.ProductVariationSwitch
                    className={style.productVariations}
                    withBottomDivider={true}
                  />
                </Slot>

                <Slot render={slots?.discontinuedHint}>
                  <ProductDetail.DiscontinuedHint />
                </Slot>
                <Slot render={slots?.price}>
                  <ProductDetail.Price
                    className={style.price}
                    hideAdditionalCosts
                  >
                    <Slot render={slots?.packagingUnitSwitch}>
                      <ProductDetail.PackagingUnitSwitch />
                    </Slot>
                  </ProductDetail.Price>
                </Slot>

                <Slot render={slots?.brandInfo}>
                  <ProductDetail.BrandInfo className={style.brandInfo} />
                </Slot>
                <Slot render={slots?.productDescription}>
                  <ProductDetail.ProductDescription
                    className={style.description}
                  />
                </Slot>
                <Slot render={slots?.attachments}>
                  <ProductDetail.Attachments className={style.attachments} />
                </Slot>
                <Slot render={slots?.simpleAttributes}>
                  <ProductDetail.SimpleAttributes />
                </Slot>
                <Slot render={slots?.smallBenefits}>
                  <SmallBenefits />
                </Slot>

                <Slot render={slots?.awards}>
                  <ProductDetail.Awards
                    className={style.awards}
                    withBottomDivider={true}
                  />
                </Slot>
                <Slot render={slots?.hazardWarnings}>
                  <ProductDetail.HazardWarnings
                    className={style.HazardWarnings}
                  />
                </Slot>
              </LYSCol>
            </LYSRow>
          )}
          {isTablet && (
            <>
              <LYSRow gutter={'lg'}>
                <LYSCol>
                  <Slot render={slots?.productInformation}>
                    <ProductDetail.ProductInformation />
                  </Slot>
                </LYSCol>
              </LYSRow>
              <LYSRow gutter={'lg'}>
                <LYSCol xs={12}>
                  {product.isNew && config.features.newProductsFeature && (
                    <ProductDetail.NewBadge />
                  )}
                  <Slot render={slots?.productImage}>
                    <ProductDetail.ProductImage
                      className={style.productImage}
                    />
                  </Slot>
                  <Slot render={slots?.productVariationSwitch}>
                    <ProductDetail.ProductVariationSwitch
                      className={style.productVariations}
                      withBottomDivider={true}
                    />
                  </Slot>

                  <Slot render={slots?.awards}>
                    <ProductDetail.Awards
                      className={style.awards}
                      withBottomDivider={true}
                    />
                  </Slot>
                  <Slot render={slots?.hazardWarnings}>
                    <ProductDetail.HazardWarnings
                      className={style.HazardWarnings}
                    />
                  </Slot>
                </LYSCol>
                <LYSCol xs={12}>
                  <Slot render={slots?.discontinuedHint}>
                    <ProductDetail.DiscontinuedHint />
                  </Slot>
                  <Slot render={slots?.price}>
                    <ProductDetail.Price className={style.price}>
                      <Slot render={slots?.packagingUnitSwitch}>
                        <ProductDetail.PackagingUnitSwitch />
                      </Slot>
                    </ProductDetail.Price>
                  </Slot>
                  <Slot render={slots?.smallBenefits}>
                    <SmallBenefits />
                  </Slot>
                </LYSCol>
              </LYSRow>

              <LYSRow gutter={'lg'}>
                <LYSCol>
                  <Slot render={slots?.productDescription}>
                    <ProductDetail.ProductDescription
                      className={style.description}
                    />
                  </Slot>
                  <Slot render={slots?.attachments}>
                    <ProductDetail.Attachments className={style.attachments} />
                  </Slot>
                  <Slot render={slots?.simpleAttributes}>
                    <ProductDetail.SimpleAttributes />
                  </Slot>
                </LYSCol>
              </LYSRow>
            </>
          )}
          {!isMobile && !isTablet && (
            <LYSRow gutter={'lg'}>
              <LYSCol lg={7}>
                {product.isNew && config.features.newProductsFeature && (
                  <ProductDetail.NewBadge />
                )}
                <Slot render={slots?.productImage}>
                  {config.features.pictureZoom.enabled ? (
                    <ProductDetail.ProductImageWithZoom
                      className={style.productImage}
                    />
                  ) : (
                    <ProductDetail.ProductImage
                      className={style.productImage}
                    />
                  )}
                </Slot>

                <Slot render={slots?.productVariationSwitch}>
                  <ProductDetail.ProductVariationSwitch
                    className={style.productVariations}
                    withBottomDivider={true}
                  />
                </Slot>

                <Slot render={slots?.awards}>
                  <ProductDetail.Awards
                    className={style.awards}
                    withBottomDivider={true}
                  />
                </Slot>
                <Slot render={slots?.hazardWarnings}>
                  <ProductDetail.HazardWarnings
                    className={style.HazardWarnings}
                  />
                </Slot>
              </LYSCol>

              <LYSCol lg={9} xl={11}>
                <Slot render={slots?.productInformation}>
                  <ProductDetail.ProductInformation />
                </Slot>

                <Slot render={slots?.brandInfo}>
                  <ProductDetail.BrandInfo className={style.brandInfo} />
                </Slot>
                <Slot render={slots?.productDescription}>
                  <ProductDetail.ProductDescription
                    className={style.description}
                  />
                </Slot>
                <Slot render={slots?.attachments}>
                  <ProductDetail.Attachments className={style.attachments} />
                </Slot>
                <Slot render={slots?.simpleAttributes}>
                  <ProductDetail.SimpleAttributes />
                </Slot>
              </LYSCol>

              <LYSCol lg={8} xl={6}>
                <Slot render={slots?.discontinuedHint}>
                  <ProductDetail.DiscontinuedHint />
                </Slot>
                <Slot render={slots?.price}>
                  <ProductDetail.Price className={style.price}>
                    <Slot render={slots?.packagingUnitSwitch}>
                      <ProductDetail.PackagingUnitSwitch />
                    </Slot>
                  </ProductDetail.Price>
                </Slot>
                <Slot render={slots?.smallBenefits}>
                  <SmallBenefits />
                </Slot>
              </LYSCol>
            </LYSRow>
          )}
          <Slot render={slots?.sparePartRelation}>
            <ProductDetail.SparePartsProductRelations />
          </Slot>
        </LYSContainer>
        <Slot render={slots?.frequentlyBoughtTogetherRecommendations}>
          <RecommendationSlider
            recommendationType={RecommendationTypes.FREQUENTLY_BOUGHT_TOGETHER}
            titleStyle={style.recommendationTitle}
            variantIds={[props.variantId]}
          />
        </Slot>
        <LYSContainer
          padded={isMobile ? false : true}
          className={style.relationContainer}
        >
          <Slot render={slots?.followUpProductRelations}>
            <ProductDetail.FollowUpProductRelations />
          </Slot>
          <Slot render={slots?.crossSellProductRelations}>
            <ProductDetail.CrossSellProductRelations />
          </Slot>
          <Slot render={slots?.productBundle}>
            <ProductDetail.ProductBundle
              className={style.productBundle}
              withTopDivider={true}
            />
          </Slot>
          {config.features.showLastVisitedProductsOnPDP && (
            <Slot render={slots?.lastVisitedPDPsRecommendations}>
              <ProductDetail.LastVisitedPDPsRecommendations />
            </Slot>
          )}
        </LYSContainer>
      </ProductDetail>
    )
  }
)

export default ProductDetailLayout
